import { Component } from 'react';
import { RouteComponentProps } from 'react-router';
import { Spinner } from 'src/components/spiner';
import { getValueBySearch } from 'src/utils/helpers';
import { LoadDataError, registerAppError } from 'src/utils/register-app-error';
import {
    decryptPayload,
    DecryptedCallInfo,
    requestCall,
    registerParticipant,
    CallReason,
    getReasons,
    getWorkTimeSettings,
    WorkTimeSettings,
    getSourceByHash,
    SourceInterface,
} from 'src/api';
import { injectAnalyticsScript } from 'src/utils/inject-analytics-script';
import ParticipantInfoSourceForm from 'src/components/participant-info-source-form';
import { SimpleCard } from '../../components/simple-card';
import { withTranslation, WithTranslation } from 'react-i18next';
import { IntroForm } from '../../components/intro-form/intro-form';
import Modal from '../../components/modal/modal';
import ZoomableIframe from 'src/components/iframe';


const Config = window.VBwidgetConfig;
type RegisterCallSourceProps = RouteComponentProps<{ sourceHash: string }> & WithTranslation;

interface RegisterCallSourcePageState {
    loading: boolean;
    decryptedCallInfo: DecryptedCallInfo | null;
    error: boolean;
    errorCode: string;
    reasons: CallReason[];
    workTimeInfo: WorkTimeSettings;
    currentForm: 'intro' | 'participant-info';
    isViible: boolean;
    source: SourceInterface;
    viewer: JSX.Element
}

class RegisterCallPageSource extends Component<RegisterCallSourceProps, RegisterCallSourcePageState> {
    private forLegalEntities: boolean;
    private forMarket: boolean;
    private forBcc: boolean

    constructor(props: RegisterCallSourceProps) {
        super(props);
        const interfaceType = window.VBwidgetConfig.interfaceType;
        this.forLegalEntities = interfaceType === 'legal-entity';
        this.forMarket = interfaceType === 'market';
        this.forBcc = interfaceType === 'bcc';
        this.setLanguage(props.location.search);
        this.state = {
            loading: true,
            decryptedCallInfo: null,
            error: false,
            errorCode: null,
            reasons: [],
            workTimeInfo: null,
            currentForm: 'intro',
            isViible: true,
            source: null,
            viewer: null
        };
    }

    public async componentDidMount() {
        const sourceHash = this.props.match.params.sourceHash;
        if (!sourceHash) {
            this.props.history.push('/call/register');
            return;
        }

        const payload = await this.checkPayload(this.props.location.search);
        const sourceResponse = await getSourceByHash(sourceHash);
        if (!sourceResponse || sourceResponse.status === 'ERROR') {
            this.props.history.push('/call/register');
            return;
        }

        const sourceFromApi = sourceResponse.source;
        injectAnalyticsScript(sourceFromApi);

        const { fieldList } = sourceFromApi;
        const langField = fieldList.find((field) => field.key === 'lang');

        if (langField) {
            const langFromApi = langField.settings.value.toLowerCase();
            this.setLanguage(`?lang=${langFromApi}`);
        }

        let workTimeInfo: WorkTimeSettings = null;
        if (!sourceFromApi.ignoreWorkTime && (!payload || !payload.operator || payload.operator === '')) {
            const companyCode = sourceFromApi.companyCode || null;
            workTimeInfo = await getWorkTimeSettings(companyCode);
        }

        await this.loadReasons();
        this.setState({ source: sourceFromApi, workTimeInfo, loading: false });

        if (sourceFromApi.showFirstPage !== undefined && !sourceFromApi.showFirstPage) {
            this.setState({ currentForm: 'participant-info' });
        }
    }

    public async componentDidUpdate(prevProps: RegisterCallSourceProps) {
        const search = this.props.location.search;
        if (prevProps.location.search !== search) {
            this.setState({ loading: true });
            this.setLanguage(search);
            await this.checkPayload(search);
        }
    }
    openFileViewer = (url: string) => {
        const viewerUrl = Config.docViewer.replace('{{url}}', url);
        const { t } = this.props;
        this.setState({
            viewer: (
                <Modal
                    header={t('modalHeader')}
                    onReject={() => {
                        this.setState({ viewer: null });
                    }}
                    content={
                        <ZoomableIframe title={t('modalHeader')} viewerUrl={viewerUrl} />
                    }
                    width="100%"
                    height="100%"
                />
            )
        });
    }

    private async loadReasons() {
        const reasonsResponse = await getReasons(this.forLegalEntities, 'source-registrator');
        if (!reasonsResponse || reasonsResponse.status === 'ERROR') {
            return;
        }

        this.setState({ reasons: reasonsResponse.list });
    }

    private setLanguage = (search: string) => {
        const query = new URLSearchParams(search);
        const lang = query.get('lang');
        if (lang) {
            this.props.i18n.changeLanguage(lang);
        }
    }

    private checkPayload = async (search: string) => {
        const payload = getValueBySearch(search, 'payload');
        if (!payload || payload === '') {
            return null;
        }

        const callInfo = await decryptPayload(payload);
        if (!callInfo || callInfo.status === 'ERROR') {
            this.setState({ loading: false });
            registerAppError(LoadDataError.PayloadIsNotValid, 'Payload is not valid.');
            return;
        }

        const isDataComplete = this.isDataComplete(callInfo.data);
        if (isDataComplete) {
            const url = await this.registerCall(callInfo.data);
            if (!url) {
                return;
            }

            this.openCallPage(url, false);
            return;
        }

        return callInfo.data;
    }

    private onDataFilled = async (data: DecryptedCallInfo) => {
        this.setState({ decryptedCallInfo: data });
        const url = await this.registerCall(data);
        if (!url) {
            return;
        }

        this.openCallPage(url, true);
    }

    private registerCall = async (data: DecryptedCallInfo) => {
        try {
            const callHash = await this.requestCall(data);
            if (!callHash) {
                this.setState({ error: true, loading: false });
                return null;
            }

            const participantInfo = await this.registerParticipant(callHash, data);
            if (!participantInfo || participantInfo.status === 'ERROR') {
                this.setState({ error: true, loading: false });
                return null;
            }

            const source = this.state.source;

            if (source.callType === 'sip' && participantInfo.sipUrl) {
                return participantInfo.sipUrl;
            }

            return participantInfo.callUrl;
        } catch (e) {
            const errorMessage = this.handleApiError(e);
            this.setState({ error: true, errorCode: errorMessage, loading: false });
            return null;
        }
    }

    private errorMap: Record<string, string> = {
        RateLimitExceeded: 'RateLimitExceeded',
        'default': 'genericError',
    };

    private handleApiError = (error: any): string => {
        const errorCode = error?.response?.data?.message || error?.message || 'default';
        return this.errorMap[errorCode] || this.errorMap.default;
    };

    private requestCall = async (data: DecryptedCallInfo) => {
        const { operator, reason } = data;
        const sourceUrl = window.location.href;
        const sourceHash = this.props.match.params.sourceHash;
        const sourceTag = getValueBySearch(this.props.location.search, 'source_tag');
        const response = await requestCall({
            reason,
            operator,
            sourceHash,
            sourceTag,
            sourceUrl
        });

        if (!response || response.status === 'ERROR') {
            return null;
        }

        return response.callHash;
    }

    private registerParticipant = async (callHash: string, data: DecryptedCallInfo) => {
        const response = await registerParticipant({
            callHash,
            ...data
        });
        return response;
    }

    private isDataComplete = (data: DecryptedCallInfo | null) => {
        if (!data) {
            return false;
        }

        const { iin, name, phone, reason } = data;
        if (!phone || !iin || !name || !reason) {
            return false;
        }

        return true;
    }

    private openCallPage = (url: string, isAgreementChecked: boolean) => {
        const lang = this.props.i18n.language;
        window.location.href = `${url}?lang=${lang}&agreementChecked=${isAgreementChecked}`;
    }

    private onAgreementChecked = () => {
        this.setState({ currentForm: 'participant-info' });
    }

    private onClose = () => {
        this.setState({ isViible: false });
        this.callIframeGoToMain();
    }

    private onGoBack = () => {
        const { source } = this.state;
        if (source.showFirstPage) {
            this.setState({ currentForm: 'intro' });
            return;
        }

        this.callIframeGoToMain();
    }

    private callIframeGoToMain = () => {
        const isInIframe = window.self !== window.top;
        if (isInIframe) {
            try {
                window.parent.postMessage('goToMain', '*');
                return Promise.resolve({ status: 'OK' });
            } catch (e) {
                return Promise.reject(`Error sending message to parent: ${e.message}`);
            }
        }
    }

    render() {
        const { t, i18n } = this.props;
        const {
            loading,
            error,
            decryptedCallInfo,
            reasons,
            workTimeInfo,
            currentForm,
            source,
            viewer
        } = this.state;
        const legalEntityClassName = this.forLegalEntities
            ? 'vb-legal-entity'
            : '';
        const marketClassName = this.forMarket
            ? 'vb-market-widget'
            : '';
        const bccClassName = this.forBcc
            ? 'vb-bcc-widget'
            : '';

        if (loading) {
            return <div className='vb-w-100 vb-h-100 vb-d-flex vb-justify-content-center vb-align-items-center'>
                <Spinner colorSheme="primary"></Spinner>
            </div>;
        }

        if (workTimeInfo && !workTimeInfo.isCallPossible) {
            return <SimpleCard title={t('title')}>
                <div style={{ maxWidth: 500 }}>{i18n.language === 'kz' ? workTimeInfo.messageKz : workTimeInfo.messageRu}</div>
            </SimpleCard>;
        }

        if (currentForm === 'intro') {
            return <IntroForm
                source={source}
                onClose={this.onClose}
                onAgreeSubmit={this.onAgreementChecked} />;
        }

        return (
            <div className={legalEntityClassName + marketClassName + bccClassName}>
                <ParticipantInfoSourceForm
                    canGoBack={(window.self !== window.top) || source.showFirstPage}
                    onGoBack={this.onGoBack}
                    reasons={reasons}
                    showOperatorCode={true}
                    error={error}
                    errorCode={this.state.errorCode}
                    onDataFilled={this.onDataFilled}
                    data={decryptedCallInfo}
                    source={source}
                    openFileViewer={this.openFileViewer}
                />
                {viewer}
            </div>
        );
    }
}

export default withTranslation('appointment')(RegisterCallPageSource);
